import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );

    textComponent?.shimmer();
  }

  /**
   * Handles line item removal.
   * @param {number} line
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(
        () => {
          this.replaceChildren(clone);
        },
        [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']
      );

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates quantity.
   *
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents =
      document.querySelectorAll('cart-items-component');

    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body })
    )
      .then((response) => {
        return response.text();
      })

      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const sectionHtml =
          parsedResponseText.sections?.[this.sectionId];

        if (!sectionHtml) {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
          });

          return;
        }

        const newSectionHTML =
          new DOMParser().parseFromString(
            sectionHtml,
            'text/html'
          );

        const newCartHiddenItemCount =
          newSectionHTML
            .querySelector('[ref="cartItemCount"]')
            ?.textContent;

        const newCartItemCount =
          newCartHiddenItemCount
            ? parseInt(newCartHiddenItemCount, 10)
            : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(
            parsedResponseText,
            this.sectionId,
            {
              itemCount: newCartItemCount,
              source: 'cart-items-component',
              sections: parsedResponseText.sections,
            }
          )
        );

        /*
         * Aggiornamento nativo della section.
         */
        morphSection(
          this.sectionId,
          sectionHtml,
          {
            mode: this.isDrawer
              ? 'hydration'
              : 'full',
          }
        );

        /*
         * ANIME BOZZOLO
         *
         * Aggiorna esplicitamente:
         * - Mystery Gift
         * - barra 1 / 2 / 3
         * - -10%
         * - -15%
         *
         * Questo evita che il blocco rewards rimanga
         * con lo stato precedente dopo + / -.
         */
        this.#refreshAnimeCartRewards(sectionHtml);

        this.#updateCartQuantitySelectorButtonStates();
      })

      .catch((error) => {
        console.error(error);
      })

      .finally(() => {
        this.#enableCartItems();

        cartPerformance.measureFromMarker(
          cartPerformaceUpdateMarker
        );
      });
  }

  /**
   * Handles discount update.
   *
   * @param {DiscountUpdateEvent} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cart errors.
   *
   * @param {number} line
   * @param {Object} parsedResponseText
   * @param {string} parsedResponseText.errors
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector =
      this.refs.quantitySelectors[line - 1];

    const quantityInput =
      quantitySelector?.querySelector('input');

    if (!quantityInput) {
      throw new Error('Quantity input not found');
    }

    quantityInput.value =
      quantityInput.defaultValue;

    const cartItemError =
      this.refs[`cartItemError-${line}`];

    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) {
      throw new Error('Cart item error not found');
    }

    if (!(cartItemErrorContainer instanceof HTMLElement)) {
      throw new Error(
        'Cart item error container not found'
      );
    }

    cartItemError.textContent =
      parsedResponseText.errors;

    cartItemErrorContainer.classList.remove(
      'hidden'
    );
  };

  /**
   * Handles cart update events.
   *
   * @param {
   *   DiscountUpdateEvent |
   *   CartUpdateEvent |
   *   CartAddEvent
   * } event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
        }
      );

      return;
    }

    if (event.target === this) return;

    const cartItemsHtml =
      event.detail?.data?.sections?.[
        this.sectionId
      ];

    if (cartItemsHtml) {
      /*
       * Aggiornamento nativo.
       */
      morphSection(
        this.sectionId,
        cartItemsHtml
      );

      /*
       * ANIME BOZZOLO
       * Aggiorna anche rewards/barra.
       */
      this.#refreshAnimeCartRewards(
        cartItemsHtml
      );

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
        }
      );
    }
  };

  /**
   * =====================================================
   * ANIME BOZZOLO — CART REWARDS REFRESH
   * =====================================================
   *
   * Prende il blocco .anime-cart-rewards dalla nuova
   * section generata da Shopify e sostituisce quello
   * attualmente visibile nel drawer.
   *
   * In questo modo:
   *
   * 1 felpa  -> 33%
   * 2 felpe  -> 66% + Gift + -10%
   * 3+ felpe -> 100% + Gift + -15%
   *
   * e funziona anche tornando indietro con il tasto -.
   *
   * @param {string} sectionHtml
   */
  #refreshAnimeCartRewards(sectionHtml) {
    if (!sectionHtml) return;

    const parsed =
      new DOMParser().parseFromString(
        sectionHtml,
        'text/html'
      );

    const newRewards =
      parsed.querySelector(
        '.anime-cart-rewards'
      );

    const currentRewards =
      document.querySelector(
        'cart-drawer-component .anime-cart-rewards'
      );

    if (!newRewards || !currentRewards) {
      return;
    }

    currentRewards.replaceWith(
      newRewards.cloneNode(true)
    );
  }

  /**
   * Disables cart items.
   */
  #disableCartItems() {
    this.classList.add(
      'cart-items-disabled'
    );
  }

  /**
   * Enables cart items.
   */
  #enableCartItems() {
    this.classList.remove(
      'cart-items-disabled'
    );
  }

  /**
   * Updates quantity selectors for all matching variants.
   *
   * @param {Object} updatedCart
   * @param {Array<{
   *   variant_id: number,
   *   quantity: number
   * }>} [updatedCart.items]
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId =
        item.variant_id.toString();

      const selectors =
        document.querySelectorAll(
          `quantity-selector-component[data-variant-id="${variantId}"]`
        );

      for (const selector of selectors) {
        const input =
          selector.querySelector(
            'input[data-cart-quantity]'
          );

        if (!input) continue;

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        if (
          'updateCartQuantity' in selector &&
          typeof selector.updateCartQuantity ===
            'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates +/- button states.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (
      const selector of
      document.querySelectorAll(
        'cart-quantity-selector-component'
      )
    ) {
      /** @type {any} */ (
        selector
      ).updateButtonStates?.();
    }
  }

  /**
   * Gets section id.
   *
   * @returns {string}
   */
  get sectionId() {
    const { sectionId } =
      this.dataset;

    if (!sectionId) {
      throw new Error(
        'Section id missing'
      );
    }

    return sectionId;
  }

  /**
   * Returns whether this is the drawer.
   *
   * @returns {boolean}
   */
  get isDrawer() {
    return (
      this.dataset.drawer !== undefined
    );
  }
}

if (
  !customElements.get(
    'cart-items-component'
  )
) {
  customElements.define(
    'cart-items-component',
    CartItemsComponent
  );
}